import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

export default function ChangePassword() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setOldPassword("");
      setNewPassword("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-2xl mx-auto" data-testid="change-password-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Security</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Change Password</h1>
        <p className="mt-2 text-sm text-[#6b655b]">
          Rotate the admin password periodically. Your current session will remain active.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-[#e5e0d8] rounded-sm p-6 md:p-8 space-y-6"
        data-testid="change-password-form"
      >
        <div className="flex items-center gap-3 text-[#234333]">
          <KeyRound size={18} strokeWidth={1.5} />
          <div className="text-sm">Authenticated as admin</div>
        </div>

        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-2">
            Current password
          </div>
          <input
            data-testid="cp-old-input"
            type="password"
            className={inputCls}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-2">
            New password
          </div>
          <input
            data-testid="cp-new-input"
            type="password"
            className={inputCls}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-2">
            Confirm new password
          </div>
          <input
            data-testid="cp-confirm-input"
            type="password"
            className={inputCls}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="cp-submit-button"
          className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm disabled:opacity-60"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
